import axios, { getHeaders } from "./axios";

interface GetImageBody {
  file: string;
}

interface UploadResponse {
  url: string;
  fileId: string;
}

/** An upload failure whose message is safe to show the user as-is. */
export class UploadError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
    this.name = "UploadError";
  }
}

/**
 * Turns an axios rejection into something a person can act on.
 *
 * 4xx bodies come from our own API (`{success,message,code}`) and are written
 * for the user — "File too large (6.2 MB, max 5 MB)" says exactly what to do.
 * 5xx messages are internal (stack traces, upstream hostnames) and must never
 * reach the screen, so they collapse to a generic retry prompt.
 */
export const uploadErrorMessage = (error: unknown): string => {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;

  if (!response) {
    return "Upload failed — check your internet connection and try again.";
  }

  const status = response.status ?? 0;

  // nginx rejects an oversized body itself, returning HTML rather than JSON.
  if (status === 413) {
    return "That image is too large. Please choose a smaller photo (max 5 MB).";
  }
  if (status === 401 || status === 403) {
    return "Your session expired. Please sign in again and retry.";
  }
  if (status === 429) {
    return "Too many uploads right now. Please wait a moment and try again.";
  }
  if (status >= 400 && status < 500) {
    const message = (response.data as { message?: unknown } | undefined)?.message;
    if (typeof message === "string" && message.trim()) return message;
    return "That image was rejected. Please try a different photo.";
  }

  return "Upload failed on the server. Please try again.";
};

const toUploadError = (error: unknown, context: string): UploadError => {
  // Keep the raw error in the console for debugging; show only the safe text.
  console.error(context, error);
  const status = (error as { response?: { status?: number } })?.response?.status;
  return new UploadError(uploadErrorMessage(error), status);
};

/**
 * Uploads a tumbler photo. Throws {@link UploadError} on failure — callers must
 * surface `error.message`. This previously returned `undefined`, which the order
 * form silently ignored, so a rejected photo looked like nothing happened.
 */
export const getImageURL = async (
  body: GetImageBody
): Promise<string | undefined> => {
  const headers = getHeaders();
  try {
    const response = await axios.post<UploadResponse>("/upload/image", body, headers);
    return response.data?.url;
  } catch (error) {
    throw toUploadError(error, "An error occurred while uploading the image");
  }
};

/**
 * Uploads a menu photo. Throws {@link UploadError} on failure so the caller can
 * show the real reason instead of a generic "Image upload failed".
 */
export const uploadMenuImage = async (
  body: { file: string; replaceFileId?: string }
): Promise<{ url: string; fileId: string } | undefined> => {
  const headers = getHeaders();
  try {
    const response = await axios.post<UploadResponse>("/upload/menu-image", body, headers);
    return response.data;
  } catch (error) {
    throw toUploadError(error, "An error occurred while uploading the menu image");
  }
};
